/**
 * PostHog telemetry for @spatius/avatarkit-rtc SDK.
 *
 * Reuses the same PostHog named instance as @spatius/avatarkit to avoid
 * duplicate SDK initializations. If avatarkit has already initialized PostHog,
 * this module piggybacks on that instance. Otherwise events are silently dropped.
 *
 * @packageDocumentation
 * @internal
 */

import { logger } from './logger';
import { trackEventOtel, setOtelVersion, flushOtelLogs } from './otel-tracker';
import { setMetricsVersion, flushMetrics } from './otel-metrics';
import { setTraceVersion, flushTraces } from './otel-trace';
import { initializeClock } from './clock';
import { recordTelemetry } from './telemetry-recorder';

// PostHog named instance (must match avatarkit's instance name)
const SDK_POSTHOG_INSTANCE_NAME = 'spatius-posthog';

// Cached PostHog instance
let posthogInstance: any = null;

// SDK metadata
let sdkVersion = '1.0.0';
let appId = '';
let environment = '';

type TelemetryKind = 'event' | 'metric';

// Telemetry queue for captures fired before PostHog is available
interface QueuedCapture {
  telemetryKind: TelemetryKind;
  event: string;
  properties: Record<string, unknown>;
  level?: 'info' | 'warning' | 'error';
}
const telemetryQueue: QueuedCapture[] = [];
const MAX_QUEUE_SIZE = 50;

// Guards the one-time pagehide/visibilitychange flush hook.
let unloadFlushInstalled = false;

// Retry timer for finding PostHog instance
let retryTimer: ReturnType<typeof setInterval> | null = null;
const RETRY_INTERVAL_MS = 500;
const MAX_RETRY_MS = 10000;

/**
 * Telemetry configuration options.
 */
export interface TelemetryConfig {
  /** SDK version string */
  sdkVersion?: string;
  /** Application ID */
  appId?: string;
  /** Environment (cn/intl) */
  environment?: string;
}

/**
 * Initialize telemetry by locating the shared PostHog instance from avatarkit.
 * Must be called after AvatarSDK.initialize().
 * @internal
 */
export function initializeTelemetry(config: TelemetryConfig): void {
  if (typeof window === 'undefined') return;

  if (config.sdkVersion) sdkVersion = config.sdkVersion;
  if (config.appId) appId = config.appId;
  if (config.environment) environment = config.environment;

  // The three OTel channels (logs, metrics, traces) travel avatarkit's global
  // providers, independent of whether the PostHog instance is found.
  setOtelVersion(sdkVersion);
  setMetricsVersion(sdkVersion);
  setTraceVersion(sdkVersion);
  // Span timestamps resolve against avatarkit's calibrated clock; start loading
  // it now so it is in place by the time the first round is reported.
  initializeClock();

  installUnloadFlush();

  posthogInstance = getPostHogInstance();
  if (posthogInstance) {
    logger.info('Telemetry', 'PostHog instance found, telemetry enabled');
    flushTelemetryQueue();
  } else {
    logger.info('Telemetry', 'PostHog instance not found yet, will retry');
    startRetry();
  }
}

/**
 * Flush on the last event the page reliably gets before it goes away.
 *
 * A session's most valuable record (`rtc_session_summary`) is emitted on
 * disconnect, and plenty of users just close the tab instead — without this the
 * whole summary dies in the queue. `pagehide` is the one teardown event that
 * fires on mobile Safari's bfcache path too, and `visibilitychange → hidden`
 * covers backgrounding, after which the page may never be resumed.
 * @internal
 */
function installUnloadFlush(): void {
  if (unloadFlushInstalled || typeof window === 'undefined') return;
  unloadFlushInstalled = true;

  const flushOnHide = (): void => {
    flushTelemetry();
  };

  try {
    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnHide();
    });
  } catch {
    // Non-browser or restricted environment; nothing to hook.
  }
}

/**
 * Periodically retry finding the PostHog instance (avatarkit may initialize later).
 * @internal
 */
function startRetry(): void {
  if (retryTimer) return;

  const startTime = Date.now();
  retryTimer = setInterval(() => {
    const instance = getPostHogInstance();
    if (instance) {
      logger.info('Telemetry', 'PostHog instance found after retry, telemetry enabled');
      flushTelemetryQueue();
      stopRetry();
    } else if (Date.now() - startTime > MAX_RETRY_MS) {
      logger.info('Telemetry', 'PostHog instance not found after retries, queued events dropped');
      telemetryQueue.length = 0;
      stopRetry();
    }
  }, RETRY_INTERVAL_MS);
}

/**
 * Stop the retry timer.
 * @internal
 */
function stopRetry(): void {
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
}

/**
 * Flush queued telemetry captures to PostHog.
 * @internal
 */
function flushTelemetryQueue(): void {
  if (!posthogInstance || telemetryQueue.length === 0) return;

  logger.info('Telemetry', `Flushing ${telemetryQueue.length} queued telemetry captures`);
  for (const queuedCapture of telemetryQueue) {
    try {
      posthogInstance.capture(
        queuedCapture.event,
        buildCaptureProperties(queuedCapture),
      );
    } catch {
      // Ignore flush errors for individual events
    }
  }
  telemetryQueue.length = 0;
}

/**
 * Try to get the shared PostHog named instance from avatarkit.
 * @internal
 */
function getPostHogInstance(): any {
  if (posthogInstance) return posthogInstance;

  try {
    // PostHog JS SDK stores named instances that can be retrieved
    // We try to get the instance that avatarkit already initialized
    const posthog = (window as any).__posthog;
    if (posthog && typeof posthog === 'object') {
      // Try direct named instance lookup
      const instance = posthog[SDK_POSTHOG_INSTANCE_NAME];
      if (instance && typeof instance.capture === 'function') {
        posthogInstance = instance;
        return instance;
      }
    }

    // Fallback: try posthog-js getInstance API
    try {
      const posthogModule = (window as any).posthog;
      if (posthogModule && typeof posthogModule.getInstance === 'function') {
        const instance = posthogModule.getInstance(SDK_POSTHOG_INSTANCE_NAME);
        if (instance && typeof instance.capture === 'function') {
          posthogInstance = instance;
          return instance;
        }
      }
    } catch {
      // getInstance not available
    }
  } catch {
    // PostHog not available
  }

  return null;
}

/**
 * Get common fields attached to all events.
 * @internal
 */
function getCommonFields(): Record<string, unknown> {
  return {
    platform: 'Web',
    sdk_version: sdkVersion,
    service_module: 'rtc',
    app_id: appId,
    env: environment,
  };
}

/**
 * Build PostHog capture properties for a telemetry payload.
 * @internal
 */
function buildCaptureProperties(
  capture: QueuedCapture,
): Record<string, unknown> {
  const baseProperties = {
    ...getCommonFields(),
    ...capture.properties,
  };

  if (capture.telemetryKind === 'metric') {
    return {
      ...baseProperties,
      telemetry_kind: 'metric',
    };
  }

  return {
    ...baseProperties,
    level: capture.level,
  };
}

/**
 * Capture or queue a telemetry payload.
 * @internal
 */
function captureTelemetry(
  capture: QueuedCapture,
): void {
  // Try to get instance if not cached yet
  const instance = posthogInstance || getPostHogInstance();
  if (!instance) {
    if (telemetryQueue.length < MAX_QUEUE_SIZE) {
      telemetryQueue.push(capture);
    }
    return;
  }

  // Instance just became available — flush any queued telemetry first
  if (telemetryQueue.length > 0) {
    flushTelemetryQueue();
    stopRetry();
  }

  try {
    instance.capture(capture.event, buildCaptureProperties(capture));
  } catch (error) {
    logger.warn(
      'Telemetry',
      'Failed to track telemetry:',
      error instanceof Error ? error.message : String(error),
    );
  }
}

/**
 * Track a telemetry event.
 * @param event - Event name
 * @param level - Log level
 * @param properties - Event-specific properties
 * @internal
 */
export function trackEvent(
  event: string,
  level: 'info' | 'warning' | 'error' = 'info',
  properties: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const fullProperties = {
    ...properties,
    timestamp: now,
  };

  recordTelemetry('event', event, { level, props: fullProperties });

  captureTelemetry({
    telemetryKind: 'event',
    event,
    level,
    properties: fullProperties,
  });

  // Dual-emit to OTel logs (avatarkit's global LoggerProvider → OpenObserve).
  trackEventOtel(event, level, properties, now);
}

/**
 * Track a telemetry metric.
 * @param metric - Metric name
 * @param properties - Metric properties
 * @internal
 */
export function trackMetric(
  metric: string,
  properties: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  captureTelemetry({
    telemetryKind: 'metric',
    event: metric,
    properties: {
      ...properties,
      timestamp: now,
    },
  });

  // Dual-emit to OTel logs (avatarkit's global LoggerProvider → OpenObserve).
  trackEventOtel(metric, 'info', properties, now);
}

/**
 * Flush pending telemetry on all four channels (for page close / disconnect).
 *
 * The OTel channels are flushed unconditionally: they travel avatarkit's global
 * providers, which are entirely independent of whether a PostHog instance was
 * ever found. Returning early on a missing PostHog instance — as this used to —
 * meant that in any app without PostHog, nothing was ever flushed at all.
 * @internal
 */
export function flushTelemetry(): void {
  flushOtelLogs();
  flushMetrics();
  flushTraces();

  const instance = posthogInstance || getPostHogInstance();
  if (!instance) return;

  try {
    flushTelemetryQueue();
    if (typeof instance.flush === 'function') {
      instance.flush();
    }
  } catch {
    // Ignore flush errors
  }
}
